import { APIExportStartReturnType } from 'src/models/BaseApi';
import { ApiFunctionType, httpPost, httpPostCancellable } from 'src/utils/http';
import { AccountOrganizerContributionsDonationsExportStatusRequestType } from 'src/models/Account/Organizer/contributions/donations';

export type AnyExportStatusReturnType = APIExportStartReturnType & {
  IsInProgress: boolean;
};

const getPath = (path: string) => `/taskstatus/${path}`;

const baseExportStatus = (url: string) => (): Promise<AnyExportStatusReturnType> => httpPost(getPath(url));

/** status of exporting */
export const apiOrganizationStaffExportStatus = (id: string): Promise<AnyExportStatusReturnType> =>
  httpPost(getPath('isorganizationstaffsexportinprogress'), {
    organizationPermanentUrl: id,
  });

export const apiDonationPagesExportStatus = (id: string): Promise<AnyExportStatusReturnType> =>
  httpPost(getPath('isorganizationdonationpagesexportinprogress'), {
    organizationPermanentUrl: id,
  });

export const apiDashboardContributionsExportStatus: ApiFunctionType<
  { campaignPermanentUrl: string },
  AnyExportStatusReturnType
> = httpPostCancellable(getPath('isdashboardcontributionsexportinprogress'));

export const apiDashboardMembersExportStatus: ApiFunctionType<
  { campaignPermanentUrl: string },
  AnyExportStatusReturnType
> = httpPostCancellable(getPath('isdashboardmembersexportinprogress'));

export const apiContributionExportStatus = baseExportStatus('isadministrationcontributionsexporttaskprogress');

export const apiOrganizationsExportStatus = baseExportStatus('isadministrationorganizationsexporttaskprogress');

export const apiUsersExportStatus = baseExportStatus('isadministrationusersexporttaskinprogress');

export const apiPartnerUsersExportStatus = baseExportStatus('ispartnerusersexporttaskinprogress');

export const apiPartnerOrganizationsExportStatus = baseExportStatus('ispartnerorganizationsexporttaskinprogress');

export const apiPartnerCampaignsExportStatus = baseExportStatus('ispartnercampaignsexporttaskinprogress');

export const apiAccountCheckExportUserData = baseExportStatus('isuserdataexportinprogress');

export const apiCampaignExportStatus = baseExportStatus('isadministrationcampaignsexporttaskprogress');

export const apiMyCampaignExportStatus = baseExportStatus('isorganizermycampaignsexportinprogress');

export const apiSupportersExportStatus = (organizationPermanentUrl: number) =>
  httpPost(getPath('isorganizationsupportersexportinprogress'), { organizationPermanentUrl });

export const apiOrganizationDonorProfileRecurringDonationsExportStatus = (profileID: number) =>
  httpPost(getPath('IsExportOrganizationDonorProfileRecurringDonationsInProgress'), { profileID });

export const apiOrganizationDonorProfileOneTimeDonationsExportStatus = (profileID: number) =>
  httpPost(getPath('IsExportOrganizationDonorProfileOneTimeDonationsInProgress'), { profileID });

export const apiOrganizerPhysicalRaffleTicketsExportStatus = (
  campaignPermanentUrl: string
): Promise<APIExportStartReturnType> =>
  httpPost(getPath('isorganizerphysicalraffleticketsexportinprogress'), { campaignPermanentUrl });

export const apiOrganizerPhysicalSweepstakeTicketsExportStatus = (
  campaignPermanentUrl: string
): Promise<APIExportStartReturnType> =>
  httpPost(getPath('isorganizerphysicalsweepstaketicketsexportinprogress'), { campaignPermanentUrl });

export const apiPartnerContributionsExportStatus = baseExportStatus('ispartnercontributionsexporttaskprogress');

export const apiExternalContributionsStatus = (campaignPermanentUrl: string): Promise<APIExportStartReturnType> =>
  httpPost(getPath('isorganizerexternalcontributionsexportinprogress'), { campaignPermanentUrl });

export const apiOrganizerContributionsDonationsStatus: ApiFunctionType<
  AccountOrganizerContributionsDonationsExportStatusRequestType,
  APIExportStartReturnType
> = httpPostCancellable(getPath('IsExportOrganizerDonationsInProgress'));

export const apiOrganizerRecurringContributionsStatus = (
  campaignPermanentUrl: string
): Promise<APIExportStartReturnType> =>
  httpPost(getPath('IsOrganizerRecurringContributionsExportInProgress'), { campaignPermanentUrl });

export const apiOrganizerSupportersStatus = (campaignPermanentUrl: string) =>
  httpPost(getPath('IsOrganizerSupportersExportInProgress'), { campaignPermanentUrl });

export const apiOrganizerRegisteredDonorsStatus = (campaignPermanentUrl: string) =>
  httpPost(getPath('IsOrganizerRegisteredDonorsExportInProgress'), { campaignPermanentUrl });

export const apiOrganizerDonorPromoCodesExportStatus = (
  campaignPermanentUrl: string
): Promise<APIExportStartReturnType> =>
  httpPost(getPath('IsOrganizerDonorPromoCodesExportInProgress'), { campaignPermanentUrl });

export const apiOrganizerDonorEventBalancesExportStatus: ApiFunctionType<{ campaignPermanentUrl: string }> =
  httpPostCancellable(getPath('IsExportOrganizationDonorEventBalancesInProgress'));

export const apiOrganizerEventRegistrationsStatus: ApiFunctionType<{ campaignPermanentUrl: string }> =
  httpPostCancellable(getPath('IsOrganizerEventRegistrationsExportInProgress'));

export const apiOrganizerTeamMembersStatus: ApiFunctionType<{ campaignPermanentUrl: string }> = httpPostCancellable(
  getPath('IsOrganizerTeamMembersExportInProgress')
);

export const apiOrganizerPrizeWinnersStatus: ApiFunctionType<{ campaignPermanentUrl: string }> = httpPostCancellable(
  getPath('IsOrganizerPrizeWinnersExportInProgress')
);

export const apiOrganizerManualContributionsStatus: ApiFunctionType<{ campaignPermanentUrl: string }> =
  httpPostCancellable(getPath('IsOrganizerManualContributionsExportInProgress'));

export const apiOrganizerTeamsExportStatus: ApiFunctionType<{ campaignPermanentUrl: string }> = httpPostCancellable(
  getPath('isOrganizerTeamsExportInProgress')
);

export const apiOrganizerTeamPrizeWinnersStatus: ApiFunctionType<{ campaignPermanentUrl: string }> =
  httpPostCancellable(getPath('IsOrganizerTeamPrizeWinnersExportInProgress'));

export const apiOrganizerDonationsStatus: ApiFunctionType<{ campaignPermanentUrl: string }> = httpPostCancellable(
  getPath('IsExportOrganizerDonationsInProgress')
);

export const apiEventGroupRegistrationsStatus: ApiFunctionType<{ campaignPermanentUrl: string }> = httpPostCancellable(
  getPath('IsOrganizerEventGroupRegistrationsExportInProgress')
);

export const apiOrganizerFundRequestsExportStatus: ApiFunctionType<{ campaignPermanentUrl: string }> =
  httpPostCancellable(getPath('IsOrganizerFundRequestsExportInProgress'));

export const apiOrganizerAdministratorsExportStatus: ApiFunctionType<{ campaignPermanentUrl: string }> =
  httpPostCancellable(getPath('IsOrganizerCampaignAdministratorsExportInProgress'));

export const apiOrganizerRaffleDrawingWinnersExportStatus: ApiFunctionType<{ campaignPermanentUrl: string }> =
  httpPostCancellable(getPath('IsOrganizerRaffleDrawingWinnersExportInProgress'));

export const apiOrganizerCrowdfundingRewardsExportStatue: ApiFunctionType<{ campaignPermanentUrl: string }> =
  httpPostCancellable(getPath('IsOrganizerCrowdfundingRewardsTableExportInProgress'));

export const apiOrganizerLedgersExportStatus: ApiFunctionType<{ campaignPermanentUrl: string }> = httpPostCancellable(
  getPath('IsOrganizerLedgersExportInProgress')
);

export const apiOrganizerSubLedgersExportStatus: ApiFunctionType<{ campaignPermanentUrl: string }> =
  httpPostCancellable(getPath('IsOrganizerSubLedgersExportInProgress'));

export const apiOrganizerSweepstakesDrawingWinnersExportStatus: ApiFunctionType<{ campaignPermanentUrl: string }> =
  httpPostCancellable(getPath('IsOrganizerSweepstakesDrawingWinnersExportInProgress'));
